import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import { IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  CustomerPayload,
  useCreateNewCustomer,
} from '~/features/customers/hooks/useCreateNewCustomer'
import { countryList } from '~/features/visitors/constants'
import { ControlledAutocomplete } from '~/ui/components/ControlledAutocomplete'
import { ControlledCheckbox } from '~/ui/components/ControlledCheckbox'
import { ControlledTextField } from '~/ui/components/ControlledTextField'

const formSchema = z.object({
  country: z.string().min(1, { message: 'Country is required' }),
  address: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  full_name: z.string().trim().min(1, { message: 'Name is required' }),
  can_promote: z.boolean(),
}) satisfies z.ZodType<CustomerPayload>

export const CreateCustomerDialog = () => {
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    setOpen(false)
  }

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CustomerPayload>({
    defaultValues: {
      country: '',
      full_name: '',
      email: null,
      address: null,
      phone: null,
      can_promote: false,
    },
    resolver: zodResolver(formSchema),
  })

  const { mutate: createCustomer, isPending } = useCreateNewCustomer()

  const onSubmit = (data: CustomerPayload) => {
    createCustomer(data, {
      onSuccess: () => {
        handleClose()
      },
    })
  }

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Create new
      </Button>
      <Dialog open={open} PaperProps={{ sx: { py: 2, px: 4 } }} onClose={handleClose}>
        <Stack py={1} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Create new customer</Typography>
          <IconButton onClick={handleClose}>
            <SvgIcon>
              <IconX />
            </SvgIcon>
          </IconButton>
        </Stack>
        <Stack component="form" gap={1} onSubmit={handleSubmit(onSubmit)} py={2}>
          <ControlledTextField
            control={control}
            name="full_name"
            errors={errors}
            label="Full name"
          />
          <ControlledTextField control={control} name="address" errors={errors} label="Address" />
          <Stack direction="row" gap={1}>
            <ControlledTextField control={control} name="phone" errors={errors} label="Phone" />
            <ControlledTextField control={control} name="email" errors={errors} label="Email" />
          </Stack>
          <ControlledAutocomplete
            control={control}
            name="country"
            options={countryList}
            errors={errors}
            label="Country"
          />
          <ControlledCheckbox
            control={control}
            name="can_promote"
            errors={errors}
            label="Can promote"
          />
          <Button
            variant="contained"
            type="submit"
            disabled={isPending}
            endIcon={isPending ? <CircularProgress size={14} /> : null}
          >
            Create
          </Button>
        </Stack>
      </Dialog>
    </>
  )
}
